import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { AssignReviewer } from './components/AssignReviewer';
import {
    AssignReviewerFormFields,
    assignReviewerSchema,
    type AssignReviewerFormData,
    type ReviewerAssignment,
} from './validation';

import type { Evaluation360FormNavigatorParams } from '@/navigation/Evaluation360/Evaluation360FormNavigator';
import type { User } from '@/types/directory/User';
import type { ReviewEvaluatorSelection } from '@/types/review/ReviewAudience';

import { useGetCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import { useGetUserPermissions, useGetUsersByIds } from '@/api/directory';
import {
    useUpdateReviewAudience,
    useUpdateReviewEvaluatorSelection,
} from '@/api/review-services/review';
import { assignReviewerOnNextAtom } from '@/atoms/evaluation360';
import { MAX_NODES } from '@/components/ReviewerNodes/ReviewerNodes';
import { CycleInstanceStatus } from '@/constants/review/CycleInstance';
import { useAmelioTranslations } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useError } from '@/hooks/utils/useError';
import { useRegisterPageAction } from '@/hooks/utils/useRegisterPageAction';
import { showWarningToast } from '@/plugins/Toast';
import { getReviewees } from '@/utils/reviewAudience';
import { getRevieweesTransformedData, type Reviewee } from '@/utils/reviewEvaluatorSelection';
import { validationMessageKey } from '@/utils/validationMessage';

export const AssignReviewerPage = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const { cycleInstanceId } = useParams<Evaluation360FormNavigatorParams>();

    const {
        data: cycleInstance,
        isLoading: isLoadingCycleInstance,
        isError: isCycleInstanceError,
    } = useGetCycleInstance(cycleInstanceId, {
        includeInactiveQuestions: true,
    });

    const { mutateAsync: updateReviewEvaluatorSelection, isPending: isUpdatingReviewers } =
        useUpdateReviewEvaluatorSelection();

    const { mutateAsync: updateReviewAudience, isPending: isRemovingReviewee } =
        useUpdateReviewAudience();

    const { isLoading: permissionsLoading, isError: permissionsError } = useGetUserPermissions();

    // Extract reviewee IDs from cycle instance to fetch their details
    const revieweeIds = useMemo(() => {
        const reviewAudienceAssignments =
            cycleInstance?.reviewAudience?.reviewAudienceAssignments || [];

        return [...new Set(reviewAudienceAssignments.map((assignment) => assignment.assignedTo))];
    }, [cycleInstance?.reviewAudience?.reviewAudienceAssignments]);

    // Extract reviewer IDs from cycle instance to fetch reviewers that may not be in loaded pages
    const reviewerIds = useMemo(() => {
        const reviewEvaluatorAssignments =
            cycleInstance?.reviewEvaluatorSelection?.reviewEvaluatorAssignments || [];

        return [...new Set(reviewEvaluatorAssignments.map((assignment) => assignment.assignedTo))];
    }, [cycleInstance?.reviewEvaluatorSelection?.reviewEvaluatorAssignments]);

    const requiredUserIds = useMemo(
        () => [...new Set([...revieweeIds, ...reviewerIds])],
        [revieweeIds, reviewerIds],
    );

    // Fetch required user details (reviewees + assigned reviewers) that might not be in loaded pages
    const {
        data: requiredUsers,
        isError: requiredUsersError,
        isPending: requiredUsersLoading,
    } = useGetUsersByIds({
        userIds: requiredUserIds,
        identifier: `reviewer-assignment-${cycleInstanceId}`,
    });

    const { setValue, trigger, reset, getFieldState, getValues, watch } =
        useForm<AssignReviewerFormData>({
            resolver: zodResolver(assignReviewerSchema),
            defaultValues: {
                [AssignReviewerFormFields.reviewerAssignments]: [],
            },
        });

    const reviewerAssignments = watch(AssignReviewerFormFields.reviewerAssignments);

    const baseReviewees: Reviewee[] = useMemo(() => {
        const users = requiredUsers || [];

        const reviewees = getReviewees({
            reviewAudience: cycleInstance?.reviewAudience,
            users,
        });

        return getRevieweesTransformedData({
            reviewAudience: cycleInstance?.reviewAudience,
            reviewEvaluatorSelection: cycleInstance?.reviewEvaluatorSelection,
            reviewees,
            users,
        });
    }, [cycleInstance?.reviewAudience, cycleInstance?.reviewEvaluatorSelection, requiredUsers]);

    const reviewees: Reviewee[] = useMemo(() => {
        if (!reviewerAssignments.length) {
            return baseReviewees;
        }

        return baseReviewees.map((reviewee) => {
            const assignment = reviewerAssignments.find((a) => a.revieweeId === reviewee.id);
            if (assignment) {
                return {
                    ...reviewee,
                    reviewers: assignment.reviewers,
                };
            }
            return reviewee;
        });
    }, [baseReviewees, reviewerAssignments]);

    const onAddReviewer = useCallback(
        ({ revieweeId, reviewers: reviewersToAdd }: { revieweeId: string; reviewers: User[] }) => {
            if (!reviewersToAdd.length) {
                return;
            }

            const previousAssignments =
                getValues(AssignReviewerFormFields.reviewerAssignments) || [];
            const existingEntry = previousAssignments.find(
                (assignment) => assignment.revieweeId === revieweeId,
            );

            const combinedReviewers = existingEntry ? [...existingEntry.reviewers] : [];

            reviewersToAdd.forEach((reviewer) => {
                const alreadyAssigned = combinedReviewers.some(
                    (assigned) => assigned.id === reviewer.id,
                );
                if (!alreadyAssigned) {
                    combinedReviewers.push(reviewer);
                }
            });

            if (combinedReviewers.length > MAX_NODES) {
                showWarningToast({
                    title: t('evaluation360AssignReviewers.validation.title'),
                    message: t('evaluation360AssignReviewers.validation.maxReviewers', {
                        count: MAX_NODES,
                    }),
                });
                return;
            }

            const updatedAssignments = previousAssignments.map((assignment) =>
                assignment.revieweeId === revieweeId
                    ? { ...assignment, reviewers: combinedReviewers }
                    : assignment,
            );

            if (!existingEntry) {
                const reviewee = baseReviewees.find((r) => r.id === revieweeId);
                updatedAssignments.push({
                    revieweeId,
                    reviewers: combinedReviewers,
                    reviewAudienceAssignmentId: reviewee?.reviewAudienceAssignmentId ?? '',
                });
            }

            // Only update local state, don't make API call
            setValue(AssignReviewerFormFields.reviewerAssignments, updatedAssignments, {
                shouldValidate: false,
                shouldDirty: true,
            });
        },
        [baseReviewees, getValues, setValue, t],
    );

    const onRemoveReviewer = useCallback(
        ({ revieweeId, reviewerId }: { revieweeId: string; reviewerId: string }) => {
            const previousAssignments =
                getValues(AssignReviewerFormFields.reviewerAssignments) || [];

            const updatedAssignments = previousAssignments.map((assignment) =>
                assignment.revieweeId === revieweeId
                    ? {
                          ...assignment,
                          reviewers: assignment.reviewers.filter(
                              (reviewer: User) => reviewer.id !== reviewerId,
                          ),
                      }
                    : assignment,
            );

            // Only update local state, don't make API call
            setValue(AssignReviewerFormFields.reviewerAssignments, updatedAssignments, {
                shouldValidate: false,
                shouldDirty: true,
            });
        },
        [getValues, setValue],
    );

    const onRemoveReviewee = useCallback(
        async (revieweeId: string) => {
            if (!cycleInstance?.reviewAudience) {
                return;
            }

            const updatedAssignments =
                cycleInstance.reviewAudience.reviewAudienceAssignments.filter(
                    (assignment) => assignment.assignedTo !== revieweeId,
                );

            try {
                await updateReviewAudience({
                    ...cycleInstance.reviewAudience,
                    reviewAudienceAssignments: updatedAssignments,
                });

                const previousAssignments =
                    getValues(AssignReviewerFormFields.reviewerAssignments) || [];

                setValue(
                    AssignReviewerFormFields.reviewerAssignments,
                    previousAssignments.filter(
                        (assignment) => assignment.revieweeId !== revieweeId,
                    ),
                    { shouldValidate: false, shouldDirty: true },
                );
            } catch (error) {
                handleError({ error });
            }
        },
        [cycleInstance?.reviewAudience, updateReviewAudience, getValues, setValue, handleError],
    );

    const onNext = useCallback(async () => {
        const isValid = await trigger(AssignReviewerFormFields.reviewerAssignments);

        if (!isValid) {
            const fieldState = getFieldState(AssignReviewerFormFields.reviewerAssignments);
            const errorMessage = validationMessageKey(fieldState.error?.message);

            showWarningToast({
                title: t('evaluation360AssignReviewers.validation.title'),
                message: errorMessage
                    ? t(errorMessage)
                    : t('evaluation360AssignReviewers.validation.minReviewers'),
            });

            return false;
        }

        try {
            const currentReviewerAssignments = getValues(
                AssignReviewerFormFields.reviewerAssignments,
            );

            const reviewEvaluatorSelection: ReviewEvaluatorSelection = {
                id: cycleInstance?.reviewEvaluatorSelection?.id || '',
                cycleInstanceId: cycleInstance?.id || '',
                reviewEvaluatorAssignments: currentReviewerAssignments.flatMap((assignment) =>
                    assignment.reviewers.map((reviewer) => ({
                        assignedTo: reviewer.id,
                        reviewEvaluatorSelectionId:
                            cycleInstance?.reviewEvaluatorSelection?.id || '',
                        reviewAudienceAssignmentId: assignment.reviewAudienceAssignmentId,
                    })),
                ),
                createdAt: cycleInstance?.reviewEvaluatorSelection?.createdAt || '',
                updatedAt: new Date().toISOString(),
            };

            await updateReviewEvaluatorSelection(reviewEvaluatorSelection);

            return true;
        } catch (error) {
            handleError({ error });

            return false;
        }
    }, [trigger, getFieldState, t, cycleInstance, getValues, handleError]);

    useRegisterPageAction(assignReviewerOnNextAtom, onNext);

    useFetchErrorsHandler({
        errors: [isCycleInstanceError, permissionsError, requiredUsersError],
    });

    useEffect(() => {
        if (!baseReviewees.length || !cycleInstance?.reviewEvaluatorSelection) {
            return;
        }

        // The cycle is refetched after a reviewee is removed: reviewers picked for the other
        // reviewees are only saved on "Next", so they must survive the new server snapshot.
        const unsavedAssignments = getValues(AssignReviewerFormFields.reviewerAssignments);

        const normalizedAssignments: ReviewerAssignment[] = baseReviewees.map((reviewee) => {
            const unsavedAssignment = unsavedAssignments.find(
                (assignment) =>
                    assignment.reviewAudienceAssignmentId === reviewee.reviewAudienceAssignmentId,
            );

            return {
                revieweeId: reviewee.id,
                reviewers: unsavedAssignment?.reviewers ?? reviewee.reviewers,
                reviewAudienceAssignmentId: reviewee.reviewAudienceAssignmentId,
            };
        });

        reset({
            [AssignReviewerFormFields.reviewerAssignments]: normalizedAssignments,
        });
    }, [baseReviewees, cycleInstance?.reviewEvaluatorSelection, getValues, reset]);

    const isLoading = isLoadingCycleInstance || permissionsLoading || requiredUsersLoading;
    const isEvaluationLaunched = cycleInstance?.status === CycleInstanceStatus.LAUNCHED;

    return (
        <AssignReviewer
            reviewees={reviewees}
            isLoading={isLoading}
            isSaving={isUpdatingReviewers || isRemovingReviewee}
            isEvaluationLaunched={isEvaluationLaunched}
            onAddReviewer={onAddReviewer}
            onRemoveReviewer={onRemoveReviewer}
            onRemoveReviewee={onRemoveReviewee}
        />
    );
};
